import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { IconComponent } from '@libs/components/icons';
import { CapacitorService, EnvironmentService } from '@libs/shared/common';
import { ViewportService } from '@libs/shared/services';

interface StoreLink {
  href: string;
  badgeSrc: string;
  badgeAlt: string;
}

@Component({
  selector: 'ui-app-store-banner',
  templateUrl: './app-store-banner.component.html',
  styleUrls: ['./app-store-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, IconComponent],
})
export class AppStoreBannerComponent {
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly envService: EnvironmentService = inject(EnvironmentService);
  private readonly isDesktop: Signal<boolean> = toSignal(inject(ViewportService).isDesktop(), { initialValue: false });
  private readonly dismissed: WritableSignal<boolean> = signal(false);

  protected readonly store: Signal<StoreLink> = computed(() =>
    this.capacitorService.isAndroidDevice()
      ? {
        href: this.envService.links().playStoreUrl,
        badgeSrc: 'https://play.google.com/intl/en_us/badges/static/images/badges/de_badge_web_generic.png',
        badgeAlt: 'Jetzt bei Google Play',
      }
      : {
        href: `https://apps.apple.com/app/id${this.envService.msalConfig().iosAppStoreId}`,
        badgeSrc: './assets/img/footer/app-store-logo.png',
        badgeAlt: 'Im App Store laden',
      },
  );

  public readonly visible: Signal<boolean> = computed(
    () => !this.dismissed() && !this.capacitorService.isCapacitorApp() && !this.isDesktop() && this.usesCustomPromotionBanner(),
  );

  protected dismiss(): void {
    this.dismissed.set(true);
  }

  private usesCustomPromotionBanner(): boolean {
    return (
      this.capacitorService.isAndroidDevice() ||
      (this.capacitorService.isIOSDevice() && !this.capacitorService.isSafariBrowser())
    );
  }
}
